"use client";

import * as SolidIcons from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as HeroIcons from "@heroicons/react/20/solid";
import { ReactElement, useMemo, useState } from "react";

import { iconsSchemas } from "../../config/entities";
import { IconDefinition } from "../../utils/icon-utils";
import { Button } from "../catalyst/Button";
import { IconPicker } from "./IconPicker";

const getHeroIconSet = (): IconDefinition[] =>
  Object.entries(HeroIcons).map(([key, Icon]) => {
    const IconComponent = Icon as React.FC<{ className?: string }>;
    return {
      label: key.replace(/([A-Z])/g, " $1").trim(),
      name: key,
      render: () => <IconComponent className="h-5 w-5" />,
    };
  });

const getFontAwesomeIconSet = (): IconDefinition[] =>
  Object.entries(SolidIcons)
    .filter(([key, val]) => key.startsWith("fa") && typeof val === "object")
    .map(([key, icon]) => ({
      label: key
        .replace(/^fa/, "")
        .replace(/([A-Z])/g, " $1")
        .trim(),
      name: key,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      render: () => <FontAwesomeIcon className="h-5 w-5" icon={icon as any} />,
    }));

const getCustomIconSet = (): IconDefinition[] =>
  Object.entries(iconsSchemas).map(([key, { component: Component }]) => ({
    label: key,
    name: key,
    render: () => <Component className="h-5 w-5" />,
  }));

type IconSource = "custom" | "fontawesome" | "heroicons";

export const IconSelectField = ({
  iconSource = ["fontawesome"],
  onChange,
  value,
}: {
  iconSource: IconSource[];
  onChange: (val: string) => void;
  value: string;
}): ReactElement => {
  const [open, setOpen] = useState(false);

  const iconSet = useMemo(() => {
    const sets: IconDefinition[] = [];
    if (iconSource.includes("fontawesome"))
      sets.push(...getFontAwesomeIconSet());
    if (iconSource.includes("heroicons")) sets.push(...getHeroIconSet());
    if (iconSource.includes("custom")) sets.push(...getCustomIconSet());
    return sets;
  }, [iconSource]);

  const selected = iconSet.find((i) => i.name === value);

  return (
    <>
      <Button className="mt-1 w-full" onClick={() => setOpen(true)} outline>
        {selected ? (
          <>
            {selected.render()}
            {selected.label}
          </>
        ) : (
          "Select Icon"
        )}
      </Button>

      <IconPicker
        iconSet={iconSet}
        onChange={onChange}
        open={open}
        setOpen={setOpen}
      />
    </>
  );
};
